/**
 * OverrideModal - structured override reason capture (PROD-571).
 *
 * Replaces the static prototype's free-text override (a Learning Loop
 * event line; see HTML-BEHAVIORAL-EXTRACTION.md gotcha 7) with a
 * structured modal. The modal is the canonical Layer 4 surface for
 * clinician overrides per ADR-001 and feeds the learning loop event
 * stream surfaced in PROD-573.
 *
 * Form fields per the ticket spec (verbatim labels):
 *   1. Reason category (required, single-select):
 *      Patient preference / Contraindication not visible to AI /
 *      Insurance constraint / Clinical context / Workflow constraint /
 *      Other
 *   2. Alternative considered (optional, free text, max 200 chars)
 *   3. Patient context emphasized (optional, multi-select tags):
 *      comorbidity / medication interaction / social determinant /
 *      cultural factor / clinical history / other
 *   4. Free text reason (required, max 200 chars)
 *
 * The verbatim label strings live in `src/types/override.ts` as
 * `REASON_CATEGORIES` and `PATIENT_CONTEXT_TAGS`. Submit validates the
 * payload against the Zod schema and dispatches through
 * `useOverrideStore.submitOverride` which:
 *   1. Re-runs Zod parse (defense in depth).
 *   2. Persists tenant-scoped via `cps:tenant:${tenantId}:overrides`.
 *   3. Emits `override_event` telemetry with the structured payload
 *      (HC9 telemetry-real - asserted in
 *      tests/override-modal.test.tsx + tests/override-store.test.ts).
 *
 * Signal DS sourcing per COMPONENT-CONTRACTS §8:
 *   - Modal shell: Component (Modal) [GAP: signal-ds-package]
 *     - tracked as G8 in docs/SIGNAL-DS-CONTRIBUTIONS.md until Signal
 *       DS publishes the canonical Modal primitive. Hand-rolled here as
 *       a portal-free `<div role="dialog" aria-modal="true">` + the
 *       FocusTrap util in src/components/FocusTrap.tsx.
 *   - Form layout: Block (Form-vertical) [GAP via G8]
 *   - Reason category radio group: Component (RadioGroup) - native
 *     `<input type="radio">` styled with Signal DS tokens
 *   - Free text input: Component (Textarea variant `bounded`) - native
 *     `<textarea>` with character counter + max length
 *   - Context tags multi-select: Component (TagSelect) [GAP via G8] -
 *     native `<button role="checkbox" aria-checked>` per WAI-ARIA
 *     multi-selectable group pattern
 *   - Submit / Cancel buttons: Component (Button) - DS-existing
 *
 * HC2: Tailwind classes resolve through the Signal DS token map in
 * src/styles/tokens.ts. No raw hex.
 *
 * HC5: zero em dashes anywhere in the rendered copy or in this comment
 * block. Reason-category and context-tag labels use hyphens; the
 * canonical strings come from the ticket spec verbatim.
 *
 * HC6 a11y:
 *   - `role="dialog" aria-modal="true"` on the shell.
 *   - `aria-labelledby` -> the canonical title node.
 *   - `aria-describedby` -> the modal description.
 *   - Focus trap captures Tab + Shift+Tab inside the shell.
 *   - ESC closes the modal (`onClose` prop).
 *   - Focus returns to the trigger when the modal closes.
 *   - First interactive element auto-focuses on open (the first reason-
 *     category radio).
 *   - Field labels are associated via `htmlFor` / `id` pairs.
 *   - Error messages are placed in `role="alert" aria-live="polite"`
 *     containers so screen readers announce them on submit.
 *   - Char counters carry `aria-live="polite"` so screen readers
 *     announce the remaining count as the user types.
 *
 * Determinism: form state is synchronous; submit is synchronous after
 * Zod parse; persist + telemetry are both synchronous Zustand actions.
 * No animations, no timeouts. The 30-second target is a UX SLA tested
 * manually; the code path is fast (the synthetic-fill test in
 * tests/override-modal.test.tsx asserts the entire flow runs in well
 * under 2s).
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  ALTERNATIVE_MAX_LENGTH,
  FREE_TEXT_MAX_LENGTH,
  PATIENT_CONTEXT_TAGS,
  REASON_CATEGORIES,
  overrideFormSchema,
  type OverrideEvent,
  type OverrideFormValues,
  type PatientContextTagId,
  type ReasonCategoryId,
} from '../types/override';
import { useOverrideStore } from '../state/override.store';
import { useFocusTrap } from './FocusTrap';

export interface OverrideModalProps {
  /** Controlled-open prop. Caller owns the open/close lifecycle. */
  open: boolean;
  /**
   * Called when the user dismisses the modal (Cancel button, ESC, or
   * clicking the backdrop). Caller must flip `open` to false.
   */
  onClose: () => void;
  /** Pathway id the override belongs to. */
  pathwayId: string;
  /** Step id the NBA was surfaced from. */
  stepId: string;
  /** NBA id being overridden. */
  nbaId: string;
  /** Optional title for the NBA being overridden (visible context). */
  nbaTitle?: string;
  /**
   * Called after a successful submit + persist + telemetry emit. Caller
   * decides what to do with the captured event (close the modal, show a
   * toast, navigate to the learning-loop stream, etc.).
   */
  onSubmitted?: (event: OverrideEvent) => void;
}

/** Verbatim modal title - HC5 hyphen, never em dash. */
const MODAL_TITLE = 'Override AI suggestion';
const MODAL_DESCRIPTION =
  'Capture why this suggestion does not fit. Structured reasons feed the learning loop without over-suppressing future suggestions.';

const SUBMIT_LABEL = 'Submit override';
const CANCEL_LABEL = 'Cancel';
const REASON_CATEGORY_LABEL = 'Reason category';
const FREE_TEXT_LABEL = 'Free text reason';
const ALTERNATIVE_LABEL = 'Alternative considered';
const CONTEXT_TAGS_LABEL = 'Patient context emphasized';
const REASON_CATEGORY_HINT = 'Required. Select one.';
const FREE_TEXT_HINT = 'Required. Up to 200 characters.';
const ALTERNATIVE_HINT = 'Optional. Up to 200 characters.';
const CONTEXT_TAGS_HINT = 'Optional. Select any that apply.';
const PERSIST_BANNER_DISMISS_LABEL = 'Dismiss override persistence warning';

interface CharCounterProps {
  current: number;
  max: number;
  fieldId: string;
}

function CharCounter({ current, max, fieldId }: CharCounterProps) {
  const remaining = max - current;
  const overLimit = current > max;
  return (
    <span
      id={`${fieldId}-counter`}
      aria-live="polite"
      data-testid={`${fieldId}-counter`}
      data-over-limit={overLimit ? 'true' : 'false'}
      className={[
        'text-signal-xs',
        overLimit ? 'text-warning-strong-fg font-semibold' : 'text-text-tertiary',
      ].join(' ')}
    >
      {remaining} characters left
    </span>
  );
}

interface FieldErrorProps {
  id: string;
  message?: string;
}

function FieldError({ id, message }: FieldErrorProps) {
  return (
    <span
      id={id}
      role="alert"
      aria-live="polite"
      data-testid={id}
      className="block text-signal-xs font-semibold text-warning-strong-fg"
    >
      {message ?? ''}
    </span>
  );
}

export function OverrideModal({
  open,
  onClose,
  pathwayId,
  stepId,
  nbaId,
  nbaTitle,
  onSubmitted,
}: OverrideModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const reasonGroupId = useId();
  const reasonErrorId = `${reasonGroupId}-error`;
  const freeTextId = useId();
  const freeTextErrorId = `${freeTextId}-error`;
  const alternativeId = useId();
  const alternativeErrorId = `${alternativeId}-error`;
  const contextTagsId = useId();

  const containerRef = useRef<HTMLDivElement>(null);

  const submitOverride = useOverrideStore((s) => s.submitOverride);
  const persistError = useOverrideStore((s) => s.persistError);
  const clearPersistError = useOverrideStore((s) => s.clearPersistError);

  const {
    control,
    handleSubmit,
    register,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<OverrideFormValues>({
    defaultValues: {
      reason_category: undefined as unknown as ReasonCategoryId,
      free_text_reason: '',
      alternative_considered: '',
      context_tags: [],
    },
    mode: 'onSubmit',
  });

  // Reset form whenever the modal re-opens so a previous draft does
  // not bleed into the next override session.
  useEffect(() => {
    if (open) {
      reset({
        reason_category: undefined as unknown as ReasonCategoryId,
        free_text_reason: '',
        alternative_considered: '',
        context_tags: [],
      });
      // Also clear any persistError carried from a previous failed
      // submit so the user starts with a clean slate.
      clearPersistError();
    }
  }, [open, reset, clearPersistError]);

  useFocusTrap(containerRef, { enabled: open, onEscape: onClose });

  const freeTextValue = watch('free_text_reason') ?? '';
  const alternativeValue = watch('alternative_considered') ?? '';

  const reasonOptions = useMemo(() => REASON_CATEGORIES, []);
  const tagOptions = useMemo(() => PATIENT_CONTEXT_TAGS, []);

  const onSubmit = handleSubmit((values) => {
    // Defense-in-depth Zod parse at the form boundary. The store also
    // parses but surfacing errors in the form gives the user a target.
    const parsed = overrideFormSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (typeof path === 'string') {
          setError(path as keyof OverrideFormValues, {
            type: 'validation',
            message: issue.message,
          });
        }
      }
      return;
    }

    const result = submitOverride({
      pathwayId,
      stepId,
      nbaId,
      form: parsed.data,
    });

    if (!result.ok) {
      if (result.reason === 'validation') {
        // Schema rejected at the store; surface as a generic free-text
        // error so the user sees something actionable.
        setError('free_text_reason', {
          type: 'validation',
          message: result.error,
        });
      }
      // Persistence failure surfaces via `persistError` (rendered below);
      // the modal stays open so the user can retry.
      return;
    }

    onSubmitted?.(result.event);
    onClose();
  });

  if (!open) return null;

  const reasonError = errors.reason_category?.message;
  const freeTextError = errors.free_text_reason?.message;
  const alternativeError = errors.alternative_considered?.message;

  return (
    <div
      data-testid="override-modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-text-primary/60 px-gutter-md py-gutter-md"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        data-testid="override-modal"
        tabIndex={-1}
        className="w-full max-w-xl rounded-signal-lg bg-surface-base shadow-signal-elevated ring-1 ring-surface-border"
      >
        <header className="flex flex-col gap-1 border-b border-surface-border px-gutter-lg py-gutter-md">
          <h2
            id={titleId}
            className="text-signal-xl font-semibold text-text-primary"
          >
            {MODAL_TITLE}
          </h2>
          {nbaTitle ? (
            <p
              data-testid="override-modal-nba-title"
              className="text-signal-sm text-text-secondary"
            >
              Overriding: {nbaTitle}
            </p>
          ) : null}
          <p
            id={descriptionId}
            className="text-signal-sm text-text-secondary"
          >
            {MODAL_DESCRIPTION}
          </p>
        </header>

        <form
          onSubmit={onSubmit}
          noValidate
          className="flex flex-col gap-gutter-md px-gutter-lg py-gutter-md"
        >
          {persistError !== null ? (
            <div
              role="alert"
              data-testid="override-persist-error"
              className="flex items-start gap-gutter-sm rounded-signal-md border border-warning-strong-border bg-warning-strong-bg px-gutter-md py-gutter-sm text-signal-sm text-warning-strong-fg"
            >
              <span aria-hidden="true" className="font-semibold uppercase tracking-wide">
                Warning
              </span>
              <span className="flex-1">{persistError}</span>
              <button
                type="button"
                onClick={clearPersistError}
                className="text-signal-xs font-semibold uppercase tracking-wide underline focus:outline-none focus:ring-2 focus:ring-warning-strong"
                aria-label={PERSIST_BANNER_DISMISS_LABEL}
              >
                Dismiss
              </button>
            </div>
          ) : null}

          <fieldset
            className="flex flex-col gap-gutter-sm"
            aria-describedby={`${reasonGroupId}-hint ${reasonError ? reasonErrorId : ''}`.trim()}
            aria-invalid={reasonError ? 'true' : 'false'}
          >
            <legend className="text-signal-sm font-semibold text-text-primary">
              {REASON_CATEGORY_LABEL}
              <span aria-hidden="true" className="ml-1 text-warning-strong-fg">
                *
              </span>
            </legend>
            <span
              id={`${reasonGroupId}-hint`}
              className="text-signal-xs text-text-tertiary"
            >
              {REASON_CATEGORY_HINT}
            </span>
            <div
              role="radiogroup"
              aria-labelledby={`${reasonGroupId}-label`}
              data-testid="override-reason-category-group"
              className="flex flex-col gap-1"
            >
              <span id={`${reasonGroupId}-label`} className="sr-only">
                {REASON_CATEGORY_LABEL}
              </span>
              {reasonOptions.map((option) => {
                const inputId = `${reasonGroupId}-${option.id}`;
                return (
                  <label
                    key={option.id}
                    htmlFor={inputId}
                    className="flex cursor-pointer items-center gap-gutter-sm rounded-signal-sm px-1 py-1 text-signal-sm text-text-primary hover:bg-surface-muted"
                  >
                    <input
                      id={inputId}
                      type="radio"
                      value={option.id}
                      data-testid={`override-reason-${option.id}`}
                      {...register('reason_category', {
                        required: 'Reason category is required',
                      })}
                      className="h-4 w-4 accent-signal-primary-600"
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
            <FieldError id={reasonErrorId} message={reasonError} />
          </fieldset>

          <div className="flex flex-col gap-gutter-sm">
            <label
              htmlFor={freeTextId}
              className="text-signal-sm font-semibold text-text-primary"
            >
              {FREE_TEXT_LABEL}
              <span aria-hidden="true" className="ml-1 text-warning-strong-fg">
                *
              </span>
            </label>
            <span
              id={`${freeTextId}-hint`}
              className="text-signal-xs text-text-tertiary"
            >
              {FREE_TEXT_HINT}
            </span>
            <textarea
              id={freeTextId}
              data-testid="override-free-text-reason"
              maxLength={FREE_TEXT_MAX_LENGTH}
              rows={3}
              aria-describedby={`${freeTextId}-hint ${freeTextId}-counter ${freeTextError ? freeTextErrorId : ''}`.trim()}
              aria-invalid={freeTextError ? 'true' : 'false'}
              {...register('free_text_reason', {
                required: 'Free text reason is required',
                maxLength: {
                  value: FREE_TEXT_MAX_LENGTH,
                  message: 'Free text reason must be at most 200 characters',
                },
              })}
              className="w-full rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-gutter-sm text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            />
            <CharCounter
              current={freeTextValue.length}
              max={FREE_TEXT_MAX_LENGTH}
              fieldId={freeTextId}
            />
            <FieldError id={freeTextErrorId} message={freeTextError} />
          </div>

          <div className="flex flex-col gap-gutter-sm">
            <label
              htmlFor={alternativeId}
              className="text-signal-sm font-semibold text-text-primary"
            >
              {ALTERNATIVE_LABEL}
            </label>
            <span
              id={`${alternativeId}-hint`}
              className="text-signal-xs text-text-tertiary"
            >
              {ALTERNATIVE_HINT}
            </span>
            <textarea
              id={alternativeId}
              data-testid="override-alternative-considered"
              maxLength={ALTERNATIVE_MAX_LENGTH}
              rows={2}
              aria-describedby={`${alternativeId}-hint ${alternativeId}-counter ${alternativeError ? alternativeErrorId : ''}`.trim()}
              aria-invalid={alternativeError ? 'true' : 'false'}
              {...register('alternative_considered')}
              className="w-full rounded-signal-md border border-surface-border bg-surface-base px-gutter-sm py-gutter-sm text-signal-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            />
            <CharCounter
              current={alternativeValue.length}
              max={ALTERNATIVE_MAX_LENGTH}
              fieldId={alternativeId}
            />
            <FieldError id={alternativeErrorId} message={alternativeError} />
          </div>

          <fieldset className="flex flex-col gap-gutter-sm">
            <legend className="text-signal-sm font-semibold text-text-primary">
              {CONTEXT_TAGS_LABEL}
            </legend>
            <span
              id={`${contextTagsId}-hint`}
              className="text-signal-xs text-text-tertiary"
            >
              {CONTEXT_TAGS_HINT}
            </span>
            <Controller
              control={control}
              name="context_tags"
              render={({ field }) => (
                <ContextTagSelect
                  id={contextTagsId}
                  options={tagOptions}
                  value={field.value ?? []}
                  onChange={field.onChange}
                />
              )}
            />
          </fieldset>

          <footer className="flex items-center justify-end gap-gutter-sm pt-gutter-sm">
            <button
              type="button"
              data-testid="override-cancel-button"
              onClick={onClose}
              className="rounded-signal-md px-gutter-md py-1 text-signal-sm font-semibold text-text-secondary hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-signal-primary-500"
            >
              {CANCEL_LABEL}
            </button>
            <button
              type="submit"
              data-testid="override-submit-button"
              disabled={isSubmitting}
              className="rounded-signal-md bg-signal-primary-600 px-gutter-md py-1 text-signal-sm font-semibold text-text-inverse hover:bg-signal-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-signal-primary-500 disabled:opacity-60"
            >
              {SUBMIT_LABEL}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

interface ContextTagSelectProps {
  id: string;
  options: typeof PATIENT_CONTEXT_TAGS;
  value: PatientContextTagId[];
  onChange: (next: PatientContextTagId[]) => void;
}

function ContextTagSelect({ id, options, value, onChange }: ContextTagSelectProps) {
  const [internalRef] = useState(() => ({ current: null as HTMLDivElement | null }));
  return (
    <div
      ref={(el) => {
        internalRef.current = el;
      }}
      role="group"
      aria-label="Patient context tags"
      data-testid="override-context-tags-group"
      id={id}
      className="flex flex-wrap gap-gutter-sm"
    >
      {options.map((option) => {
        const checked = value.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            role="checkbox"
            aria-checked={checked}
            data-testid={`override-context-tag-${option.id}`}
            data-checked={checked ? 'true' : 'false'}
            onClick={() => {
              if (checked) {
                onChange(value.filter((v) => v !== option.id));
              } else {
                onChange([...value, option.id]);
              }
            }}
            className={[
              'inline-flex items-center gap-1 rounded-signal-pill px-gutter-md py-1 text-signal-xs font-semibold uppercase tracking-wide transition-colors focus:outline-none focus:ring-2 focus:ring-signal-primary-500',
              checked
                ? 'bg-signal-primary-600 text-text-inverse'
                : 'bg-surface-muted text-text-secondary hover:bg-surface-border',
            ].join(' ')}
          >
            <span aria-hidden="true">{checked ? 'x' : '+'}</span>
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
